import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  Select,
  Tabs,
  useToast,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  chronicApi,
  useChronicAction,
  useChronicConfig,
  useHeartRisk,
  useOverdue,
  useRamadan,
  useRefills,
  useRegister,
  useSuggestions,
  type AsthmaRow,
  type Bp,
  type CheckType,
  type ChronicCondition,
  type DiabetesRow,
  type Due,
  type HeartRiskInput,
  type HypertensionRow,
  type PatientRef,
  type RegisterRow,
} from '@/features/chronic/api';
import { isApiError } from '@/lib/api';
import { useUrlState } from '@/lib/url-state';
import { PageContainer } from '@/layouts/PageContainer';
import { dayLabel } from '@/pages/calendar/time';
import { clinicDayLabel } from '@/pages/ops/mc-format';
import { CarePlanDialog } from './CarePlanDialog';
import { FormDialog } from './FormDialog';
import styles from '@/pages/ops/parity.module.css';
import { clinicDate, clinicDay } from '@/lib/clinic-time';

type Tab =
  | 'diabetes'
  | 'hypertension'
  | 'asthma'
  | 'overdue'
  | 'risk'
  | 'refills'
  | 'ramadan'
  | 'enrol';

const CONDITION_LABEL: Record<ChronicCondition, string> = {
  diabetes: 'Diabetes',
  hypertension: 'Hypertension',
  asthma: 'Asthma',
};
const CHECK_LABEL: Record<CheckType, string> = {
  eye: 'Eye screening',
  foot: 'Foot check',
  uacr: 'Kidney test (urine ACR)',
  act: 'Asthma Control Test score',
  asthma_review: 'Asthma review',
};

type Open =
  | { kind: 'check'; row: RegisterRow; type: CheckType }
  | { kind: 'plan'; patient: PatientRef; row?: RegisterRow }
  | { kind: 'risk'; patient: PatientRef }
  | { kind: 'ramadan'; patient: PatientRef }
  | { kind: 'asthma'; row: AsthmaRow }
  | { kind: 'end'; row: RegisterRow }
  | { kind: 'sync'; patient: PatientRef }
  | { kind: 'approve' }
  | null;

const today = () => clinicDate();

function PatientCell({ p }: { p: PatientRef }) {
  return (
    <Link to={`/patients/${p.id}`}>
      <strong>{p.fullName}</strong>
    </Link>
  );
}

function DueCell({ d, never = 'Never done' }: { d: Due; never?: string }) {
  if (!d.dueAt) return <Badge tone="danger">{never}</Badge>;
  return d.overdue ? (
    <Badge tone="danger">Overdue since {clinicDayLabel(d.dueAt)}</Badge>
  ) : (
    <span>Due {clinicDayLabel(d.dueAt)}</span>
  );
}

function BpCell({ bp }: { bp: Bp | null }) {
  if (!bp) return <span className={styles.sub}>No BP recorded</span>;
  return (
    <>
      <strong>
        {bp.systolic}/{bp.diastolic}
      </strong>{' '}
      <Badge tone={bp.onTarget ? 'success' : 'warning'}>
        {bp.onTarget ? 'On target' : 'Above target'}
      </Badge>
      <span className={styles.sub}>{clinicDayLabel(bp.at)}</span>
    </>
  );
}

/**
 * Chronic care (Admin v2): diabetes, hypertension and asthma registers built
 * from enrolments and the clinic's own records, with overdue recalls, care
 * plans, heart risk, refill dates and the Ramadan check. Targets come from
 * the API and are shown as pending clinical sign-off until approved.
 */
export function ChronicCarePage() {
  const { hasPermission } = useSession();
  const canManage = hasPermission('chronic_care:manage');
  const [tab, setTab] = useUrlState<Tab>('tab', 'diabetes', [
    'diabetes',
    'hypertension',
    'asthma',
    'overdue',
    'risk',
    'refills',
    'ramadan',
    'enrol',
  ]);
  const [open, setOpen] = useState<Open>(null);
  const cfg = useChronicConfig();
  const overdue = useOverdue();
  const suggestions = useSuggestions();
  const close = () => setOpen(null);
  return (
    <PageContainer>
      {cfg.data && !cfg.data.approved && (
        <div className={styles.notice} role="status">
          <strong>
            Targets and recall intervals are pending clinical sign-off.
          </strong>{' '}
          {cfg.data.source}
          {canManage && (
            <>
              {' '}
              <Button size="sm" onClick={() => setOpen({ kind: 'approve' })}>
                Record sign-off
              </Button>
            </>
          )}
        </div>
      )}
      {cfg.data?.approved && (
        <p className={styles.infoNotice}>
          Targets signed off by {cfg.data.approvedBy} on{' '}
          {cfg.data.approvedAt ? clinicDayLabel(cfg.data.approvedAt) : '—'}.
        </p>
      )}
      <Tabs
        variant="bar"
        label="Chronic care"
        value={tab}
        onChange={setTab}
        items={[
          { value: 'diabetes', label: 'Diabetes' },
          { value: 'hypertension', label: 'Hypertension' },
          { value: 'asthma', label: 'Asthma' },
          { value: 'overdue', label: 'Overdue', count: overdue.data?.length },
          { value: 'risk', label: 'Heart risk' },
          { value: 'refills', label: 'Refill sync' },
          { value: 'ramadan', label: 'Ramadan' },
          {
            value: 'enrol',
            label: 'To enrol',
            count: suggestions.data?.length,
          },
        ]}
      />
      {tab === 'diabetes' && (
        <DiabetesRegister canManage={canManage} onOpen={setOpen} />
      )}
      {tab === 'hypertension' && (
        <HypertensionRegister canManage={canManage} onOpen={setOpen} />
      )}
      {tab === 'asthma' && (
        <AsthmaRegister canManage={canManage} onOpen={setOpen} />
      )}
      {tab === 'overdue' && <OverdueList canManage={canManage} />}
      {tab === 'risk' && (
        <HeartRiskList canManage={canManage} onOpen={setOpen} />
      )}
      {tab === 'refills' && (
        <RefillList canManage={canManage} onOpen={setOpen} />
      )}
      {tab === 'ramadan' && (
        <RamadanList canManage={canManage} onOpen={setOpen} />
      )}
      {tab === 'enrol' && <Suggestions canManage={canManage} />}
      {open?.kind === 'check' && (
        <CheckDialog row={open.row} type={open.type} onClose={close} />
      )}
      {open?.kind === 'plan' && (
        <CarePlanDialog
          patient={open.patient}
          row={open.row}
          canManage={canManage}
          onClose={close}
        />
      )}
      {open?.kind === 'risk' && (
        <HeartRiskDialog patient={open.patient} onClose={close} />
      )}
      {open?.kind === 'ramadan' && (
        <RamadanDialog patient={open.patient} onClose={close} />
      )}
      {open?.kind === 'asthma' && (
        <AsthmaDialog row={open.row} onClose={close} />
      )}
      {open?.kind === 'end' && <EndDialog row={open.row} onClose={close} />}
      {open?.kind === 'sync' && (
        <SyncDialog patient={open.patient} onClose={close} />
      )}
      {open?.kind === 'approve' && <ApproveDialog onClose={close} />}
    </PageContainer>
  );
}

interface RegisterProps {
  canManage: boolean;
  onOpen: (o: Open) => void;
}

function RowActions({
  row,
  canManage,
  onOpen,
  checks,
}: {
  row: RegisterRow;
  canManage: boolean;
  onOpen: (o: Open) => void;
  checks: CheckType[];
}) {
  return (
    <div className={styles.rowActions}>
      <Button
        size="sm"
        onClick={() => onOpen({ kind: 'plan', patient: row.patient, row })}
      >
        Care plan
      </Button>
      {canManage &&
        checks.map((c) => (
          <Button
            key={c}
            size="sm"
            variant="ghost"
            onClick={() => onOpen({ kind: 'check', row, type: c })}
          >
            + {CHECK_LABEL[c].split(' (')[0]}
          </Button>
        ))}
      {canManage && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onOpen({ kind: 'end', row })}
        >
          Remove
        </Button>
      )}
    </div>
  );
}

function RegisterCard<R extends RegisterRow>({
  title,
  foot,
  q,
  columns,
}: {
  title: string;
  foot?: ReactNode;
  q: ReturnType<typeof useRegister<R>>;
  columns: Column<R>[];
}) {
  return (
    <Card
      title={title}
      flush
      actions={
        <span className={styles.crumb}>
          {q.data?.rows.length ?? 0} patients
        </span>
      }
    >
      <DataTable
        caption={title}
        rows={q.data?.rows}
        rowKey={(r) => r.enrolmentId}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState
            title="No patients on this register"
            body="Enrol diagnosed patients from the To enrol tab."
          />
        }
        columns={columns}
      />
      {foot && <p className={styles.cardFoot}>{foot}</p>}
    </Card>
  );
}

function DiabetesRegister({ canManage, onOpen }: RegisterProps) {
  const q = useRegister<DiabetesRow>('diabetes');
  const c = q.data?.config.diabetes;
  return (
    <RegisterCard
      title="Diabetes register"
      q={q}
      foot={
        c &&
        `Targets: HbA1c ≤ ${c.hba1cTargetPct} %, BP ≤ ${c.bpTarget.systolic}/${c.bpTarget.diastolic}. HbA1c every ${c.hba1cEveryMonths} months; eye, foot and kidney checks every ${c.eyeEveryMonths} months.`
      }
      columns={[
        {
          key: 'p',
          header: 'Patient',
          render: (r) => <PatientCell p={r.patient} />,
        },
        {
          key: 'a1c',
          header: 'HbA1c',
          render: (r) =>
            r.hba1c.value ? (
              <>
                <strong>
                  {r.hba1c.value} {r.hba1c.unit ?? '%'}
                </strong>{' '}
                {r.hba1c.onTarget !== null &&
                  r.hba1c.onTarget !== undefined && (
                    <Badge tone={r.hba1c.onTarget ? 'success' : 'warning'}>
                      {r.hba1c.onTarget ? 'On target' : 'Above target'}
                    </Badge>
                  )}
                <span className={styles.sub}>
                  {r.hba1c.at && clinicDayLabel(r.hba1c.at)}
                </span>
              </>
            ) : (
              <Badge tone="danger">No result</Badge>
            ),
        },
        { key: 'bp', header: 'BP', render: (r) => <BpCell bp={r.bp} /> },
        { key: 'eye', header: 'Eye', render: (r) => <DueCell d={r.eye} /> },
        { key: 'foot', header: 'Foot', render: (r) => <DueCell d={r.foot} /> },
        {
          key: 'uacr',
          header: 'Kidney',
          render: (r) => <DueCell d={r.uacr} />,
        },
        {
          key: 'x',
          header: '',
          render: (r) => (
            <RowActions
              row={r}
              canManage={canManage}
              onOpen={onOpen}
              checks={['eye', 'foot', 'uacr']}
            />
          ),
        },
      ]}
    />
  );
}

function HypertensionRegister({ canManage, onOpen }: RegisterProps) {
  const q = useRegister<HypertensionRow>('hypertension');
  const c = q.data?.config.hypertension;
  return (
    <RegisterCard
      title="Hypertension register"
      q={q}
      foot={
        c &&
        `Target below ${c.bpTarget.systolic}/${c.bpTarget.diastolic} (diabetes register uses its own target). Review every ${c.reviewEveryMonths} months.`
      }
      columns={[
        {
          key: 'p',
          header: 'Patient',
          render: (r) => <PatientCell p={r.patient} />,
        },
        { key: 'bp', header: 'Latest BP', render: (r) => <BpCell bp={r.bp} /> },
        {
          key: 'rv',
          header: 'Review',
          render: (r) => <DueCell d={r.review} never="No BP recorded" />,
        },
        {
          key: 'x',
          header: '',
          render: (r) => (
            <RowActions
              row={r}
              canManage={canManage}
              onOpen={onOpen}
              checks={[]}
            />
          ),
        },
      ]}
    />
  );
}

function AsthmaRegister({ canManage, onOpen }: RegisterProps) {
  const q = useRegister<AsthmaRow>('asthma');
  const c = q.data?.config.asthma;
  return (
    <RegisterCard
      title="Asthma register"
      q={q}
      foot={
        c &&
        `Asthma Control Test ${c.actWellControlledMin} or more = well controlled. Review and action plan every ${c.reviewEveryMonths} months.`
      }
      columns={[
        {
          key: 'p',
          header: 'Patient',
          render: (r) => <PatientCell p={r.patient} />,
        },
        {
          key: 'act',
          header: 'Asthma Control Test',
          render: (r) =>
            r.act ? (
              <>
                <strong>{r.act.score ?? '—'}</strong>{' '}
                {r.act.wellControlled !== null && (
                  <Badge tone={r.act.wellControlled ? 'success' : 'warning'}>
                    {r.act.wellControlled
                      ? 'Well controlled'
                      : 'Not well controlled'}
                  </Badge>
                )}
                <span className={styles.sub}>{clinicDayLabel(r.act.at)}</span>
              </>
            ) : (
              <Badge tone="warning">Not scored</Badge>
            ),
        },
        { key: 'inh', header: 'Inhalers', render: (r) => r.inhalers ?? '—' },
        {
          key: 'plan',
          header: 'Action plan',
          render: (r) =>
            r.actionPlanAt ? (
              `Given ${dayLabel(clinicDay(r.actionPlanAt))}`
            ) : (
              <Badge tone="warning">None</Badge>
            ),
        },
        {
          key: 'rv',
          header: 'Review',
          render: (r) => <DueCell d={r.review} />,
        },
        {
          key: 'x',
          header: '',
          render: (r) => (
            <>
              <RowActions
                row={r}
                canManage={canManage}
                onOpen={onOpen}
                checks={['act', 'asthma_review']}
              />
              {canManage && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onOpen({ kind: 'asthma', row: r })}
                >
                  Inhalers & plan
                </Button>
              )}
            </>
          ),
        },
      ]}
    />
  );
}

function OverdueList({ canManage }: { canManage: boolean }) {
  const q = useOverdue();
  const toast = useToast();
  const remind = useChronicAction(chronicApi.remind);
  return (
    <Card title={`Overdue checks · ${q.data?.length ?? 0}`} flush>
      <DataTable
        caption="Overdue checks"
        rows={q.data}
        rowKey={(r) => `${r.enrolmentId}-${r.what}`}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={<EmptyState title="Nothing overdue" />}
        columns={[
          {
            key: 'p',
            header: 'Patient',
            render: (r) => <PatientCell p={r.patient} />,
          },
          {
            key: 'w',
            header: 'Check',
            render: (r) => <strong>{r.what}</strong>,
          },
          {
            key: 'd',
            header: 'Due',
            render: (r) => (
              <Badge tone="danger">
                {r.dueAt
                  ? `Overdue since ${clinicDayLabel(r.dueAt)}`
                  : 'Never done'}
              </Badge>
            ),
          },
          {
            key: 'x',
            header: '',
            render: (r) =>
              canManage && (
                <Button
                  size="sm"
                  loading={
                    remind.isPending &&
                    remind.variables?.enrolmentId === r.enrolmentId &&
                    remind.variables.what === r.what
                  }
                  onClick={() =>
                    remind
                      .mutateAsync({ enrolmentId: r.enrolmentId, what: r.what })
                      .then((m) =>
                        toast.show({
                          tone: m.status === 'suppressed' ? 'info' : 'success',
                          title:
                            m.status === 'suppressed'
                              ? 'Not sent: the patient has not allowed notifications'
                              : 'Reminder recorded (no live messaging provider yet)',
                        }),
                      )
                      .catch((err: unknown) =>
                        toast.show({
                          tone: 'error',
                          title: 'Not recorded',
                          body: isApiError(err) ? err.message : undefined,
                          error: err,
                        }),
                      )
                  }
                >
                  Remind
                </Button>
              ),
          },
        ]}
      />
      <p className={styles.cardFoot}>
        Reminders go only to patients who allow notifications. No live messaging
        provider is connected yet. Automatic recall reminders also run: the
        patient app gets one reminder when a check falls due (09:00 to 20:00,
        branch time), unless the care plan is hidden from the app; checks more
        than 14 days overdue are not sent. Which automatic reminders went out is
        not available here yet.
      </p>
    </Card>
  );
}

function HeartRiskList({ canManage, onOpen }: RegisterProps) {
  const q = useHeartRisk();
  return (
    <Card title="10-year cardiovascular risk" flush>
      <DataTable
        caption="Heart risk"
        rows={q.data?.rows}
        rowKey={(r) => r.patient.id}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={<EmptyState title="No patients on the registers" />}
        columns={[
          {
            key: 'p',
            header: 'Patient',
            render: (r) => <PatientCell p={r.patient} />,
          },
          {
            key: 'i',
            header: 'Inputs',
            render: (r) => {
              const i = r.assessment?.inputs;
              return i ? (
                <span className={styles.sub}>
                  {i.sex === 'F' ? 'Female' : 'Male'} {i.age} · cholesterol{' '}
                  {i.totalCholMmol} · HDL {i.hdlMmol} mmol/L · SBP {i.systolic}
                  {i.treated ? ' (treated)' : ''}
                  {i.smoker ? ' · smoker' : ''}
                  {i.diabetes ? ' · diabetes' : ''}
                </span>
              ) : (
                '—'
              );
            },
          },
          {
            key: 'r',
            header: 'Risk',
            render: (r) =>
              r.assessment?.riskPct != null ? (
                <>
                  <strong>{r.assessment.riskPct} %</strong>{' '}
                  <Badge
                    tone={
                      r.assessment.category === 'high'
                        ? 'danger'
                        : r.assessment.category === 'moderate'
                          ? 'warning'
                          : 'success'
                    }
                  >
                    {r.assessment.category === 'high'
                      ? 'High'
                      : r.assessment.category === 'moderate'
                        ? 'Moderate'
                        : 'Low'}
                  </Badge>
                  <span className={styles.sub}>
                    {clinicDayLabel(r.assessment.recordedAt)}
                  </span>
                </>
              ) : (
                <Badge tone="neutral">Not assessed</Badge>
              ),
          },
          {
            key: 'x',
            header: '',
            render: (r) =>
              canManage && (
                <Button
                  size="sm"
                  onClick={() => onOpen({ kind: 'risk', patient: r.patient })}
                >
                  {r.assessment ? 'Reassess' : 'Assess'}
                </Button>
              ),
          },
        ]}
      />
      <p className={styles.cardFoot}>
        Framingham general CVD score (D’Agostino 2008), computed by the server
        from the inputs you record.
      </p>
    </Card>
  );
}

function RefillList({ canManage, onOpen }: RegisterProps) {
  const q = useRefills();
  return (
    <Card title="Refill dates" flush>
      <DataTable
        caption="Refill dates"
        rows={q.data}
        rowKey={(r) => r.patient.id}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState title="No dispensed medicines for registered patients" />
        }
        columns={[
          {
            key: 'p',
            header: 'Patient',
            render: (r) => <PatientCell p={r.patient} />,
          },
          {
            key: 'm',
            header: 'Medicines and next refill',
            render: (r) => (
              <>
                {r.rows.map((m) => (
                  <div key={m.medicine}>
                    {m.medicine}:{' '}
                    {m.nextRefill ? (
                      <strong>{dayLabel(m.nextRefill)}</strong>
                    ) : (
                      <span className={styles.sub}>
                        no estimate ({m.frequency})
                      </span>
                    )}{' '}
                    {m.missed && <Badge tone="danger">Missed</Badge>}
                  </div>
                ))}
              </>
            ),
          },
          {
            key: 's',
            header: '',
            render: (r) =>
              r.refillDay ? (
                <Badge tone="success">Refill day {dayLabel(r.refillDay)}</Badge>
              ) : r.inSync ? (
                <Badge tone="success">In sync</Badge>
              ) : (
                canManage && (
                  <Button
                    size="sm"
                    onClick={() => onOpen({ kind: 'sync', patient: r.patient })}
                  >
                    Line up dates
                  </Button>
                )
              ),
          },
        ]}
      />
      <p className={styles.cardFoot}>
        Next refill = last dispensing + duration (or quantity ÷ doses per day).
        One refill day for all chronic medicines; missed refills flagged.
      </p>
    </Card>
  );
}

function RamadanList({ canManage, onOpen }: RegisterProps) {
  const q = useRamadan();
  return (
    <Card title="Ramadan fasting check · diabetes" flush>
      <DataTable
        caption="Ramadan check"
        rows={q.data}
        rowKey={(r) => r.patient.id}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={<EmptyState title="No patients on the diabetes register" />}
        columns={[
          {
            key: 'p',
            header: 'Patient',
            render: (r) => <PatientCell p={r.patient} />,
          },
          {
            key: 'r',
            header: 'Risk',
            render: (r) =>
              r.assessment ? (
                <>
                  <Badge
                    tone={
                      r.assessment.category === 'high'
                        ? 'danger'
                        : r.assessment.category === 'moderate'
                          ? 'warning'
                          : 'success'
                    }
                  >
                    {r.assessment.category[0].toUpperCase() +
                      r.assessment.category.slice(1)}{' '}
                    risk
                  </Badge>
                  <span className={styles.sub}>
                    {r.assessment.factors} ·{' '}
                    {clinicDayLabel(r.assessment.recordedAt)}
                  </span>
                </>
              ) : (
                <Badge tone="neutral">Not assessed</Badge>
              ),
          },
          {
            key: 'x',
            header: '',
            render: (r) =>
              canManage && (
                <Button
                  size="sm"
                  onClick={() =>
                    onOpen({ kind: 'ramadan', patient: r.patient })
                  }
                >
                  {r.assessment ? 'Reassess' : 'Assess'}
                </Button>
              ),
          },
        ]}
      />
      <p className={styles.cardFoot}>
        Assess 6 to 8 weeks before Ramadan with the IDF-DAR 2021 risk score; the
        doctor records the category.
      </p>
    </Card>
  );
}

function Suggestions({ canManage }: { canManage: boolean }) {
  const q = useSuggestions();
  const toast = useToast();
  const enrol = useChronicAction(chronicApi.enrol);
  return (
    <Card title="Diagnosed here, not yet on a register" flush>
      <DataTable
        caption="To enrol"
        rows={q.data}
        rowKey={(r) => `${r.patient.id}-${r.condition}`}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={<EmptyState title="Everyone diagnosed is enrolled" />}
        columns={[
          {
            key: 'p',
            header: 'Patient',
            render: (r) => <PatientCell p={r.patient} />,
          },
          {
            key: 'c',
            header: 'Register',
            render: (r) => CONDITION_LABEL[r.condition],
          },
          {
            key: 'd',
            header: 'Diagnosis',
            render: (r) => (
              <>
                <span className={styles.mono}>{r.diagnosis.code}</span>{' '}
                {r.diagnosis.description}
                <span className={styles.sub}>
                  {clinicDayLabel(r.diagnosis.at)}
                </span>
              </>
            ),
          },
          {
            key: 'x',
            header: '',
            render: (r) =>
              canManage && (
                <Button
                  size="sm"
                  variant="primary"
                  loading={
                    enrol.isPending &&
                    enrol.variables?.patientId === r.patient.id &&
                    enrol.variables.condition === r.condition
                  }
                  onClick={() =>
                    enrol
                      .mutateAsync({
                        patientId: r.patient.id,
                        condition: r.condition,
                      })
                      .then(() =>
                        toast.show({
                          tone: 'success',
                          title: `Enrolled on the ${CONDITION_LABEL[r.condition].toLowerCase()} register`,
                        }),
                      )
                      .catch((err: unknown) =>
                        toast.show({
                          tone: 'error',
                          title: 'Not enrolled',
                          body: isApiError(err) ? err.message : undefined,
                          error: err,
                        }),
                      )
                  }
                >
                  Enrol
                </Button>
              ),
          },
        ]}
      />
    </Card>
  );
}

// ---- drawers ---------------------------------------------------------------

function CheckDialog({
  row,
  type,
  onClose,
}: {
  row: RegisterRow;
  type: CheckType;
  onClose: () => void;
}) {
  const a = useChronicAction(chronicApi.check);
  const [value, setValue] = useState('');
  const [date, setDate] = useState(today());
  const act = type === 'act';
  const n = Number(value);
  const invalid =
    !date ||
    date > today() ||
    (act && (!Number.isInteger(n) || n < 5 || n > 25));
  return (
    <FormDialog
      title={`${CHECK_LABEL[type]} · ${row.patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={invalid}
      onSubmit={() =>
        a.mutateAsync({
          id: row.enrolmentId,
          checkType: type,
          value: value.trim(),
          doneAt: `${date}T00:00:00.000Z`,
        })
      }
    >
      <Field label="Date done">
        <Input
          type="date"
          max={today()}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </Field>
      <Field label={act ? 'Score (5 to 25)' : 'Result (optional)'}>
        <Input
          type={act ? 'number' : 'text'}
          value={value}
          maxLength={60}
          placeholder={act ? '' : 'e.g. No retinopathy'}
          onChange={(e) => setValue(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}

function HeartRiskDialog({
  patient,
  onClose,
}: {
  patient: PatientRef;
  onClose: () => void;
}) {
  const a = useChronicAction(chronicApi.heartRisk);
  const [v, setV] = useState<Record<keyof HeartRiskInput, string>>({
    sex: 'M',
    age: '',
    totalCholMmol: '',
    hdlMmol: '',
    systolic: '',
    treated: 'no',
    smoker: 'no',
    diabetes: 'no',
  });
  const num = (k: keyof HeartRiskInput) => Number(v[k]);
  const invalid =
    !(num('age') >= 30 && num('age') <= 79) ||
    !(num('totalCholMmol') >= 1 && num('totalCholMmol') <= 20) ||
    !(num('hdlMmol') >= 0.2 && num('hdlMmol') <= 5) ||
    !(num('systolic') >= 70 && num('systolic') <= 260);
  const yesNo = (k: 'treated' | 'smoker' | 'diabetes', label: string) => (
    <Field label={label}>
      <Select
        value={v[k]}
        onChange={(e) => setV({ ...v, [k]: e.target.value })}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' },
        ]}
      />
    </Field>
  );
  const input = (
    k: 'age' | 'totalCholMmol' | 'hdlMmol' | 'systolic',
    label: string,
    step: string,
  ) => (
    <Field label={label}>
      <Input
        type="number"
        step={step}
        value={v[k]}
        onChange={(e) => setV({ ...v, [k]: e.target.value })}
      />
    </Field>
  );
  return (
    <FormDialog
      title={`Heart risk · ${patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={invalid}
      submitLabel="Calculate and save"
      onSubmit={() =>
        a.mutateAsync({
          patientId: patient.id,
          sex: v.sex as 'F' | 'M',
          age: num('age'),
          totalCholMmol: num('totalCholMmol'),
          hdlMmol: num('hdlMmol'),
          systolic: num('systolic'),
          treated: v.treated === 'yes',
          smoker: v.smoker === 'yes',
          diabetes: v.diabetes === 'yes',
        })
      }
    >
      <Field label="Sex">
        <Select
          value={v.sex}
          onChange={(e) => setV({ ...v, sex: e.target.value })}
          options={[
            { value: 'M', label: 'Male' },
            { value: 'F', label: 'Female' },
          ]}
        />
      </Field>
      {input('age', 'Age (30 to 79)', '1')}
      {input('totalCholMmol', 'Total cholesterol (mmol/L)', '0.1')}
      {input('hdlMmol', 'HDL cholesterol (mmol/L)', '0.01')}
      {input('systolic', 'Systolic BP (mmHg)', '1')}
      {yesNo('treated', 'On BP treatment')}
      {yesNo('smoker', 'Smoker')}
      {yesNo('diabetes', 'Diabetes')}
    </FormDialog>
  );
}

function RamadanDialog({
  patient,
  onClose,
}: {
  patient: PatientRef;
  onClose: () => void;
}) {
  const a = useChronicAction(chronicApi.ramadan);
  const [category, setCategory] = useState('low');
  const [factors, setFactors] = useState('');
  return (
    <FormDialog
      title={`Ramadan check · ${patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={factors.trim().length < 2}
      onSubmit={() =>
        a.mutateAsync({ patientId: patient.id, category, factors })
      }
    >
      <p className={styles.notice}>
        Assess 6 to 8 weeks before Ramadan with the IDF-DAR 2021 risk score,
        then agree a plan. Consider: type and duration of diabetes,
        hypoglycaemia in the past 3 months, HbA1c, insulin or sulphonylurea use,
        kidney or heart disease, pregnancy, frailty, heavy physical work and how
        last Ramadan went.
      </p>
      <Field label="IDF-DAR risk category">
        <Select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          options={[
            { value: 'low', label: 'Low' },
            { value: 'moderate', label: 'Moderate' },
            { value: 'high', label: 'High' },
          ]}
        />
      </Field>
      <Field label="Main factors">
        <Input
          value={factors}
          maxLength={300}
          onChange={(e) => setFactors(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}

function AsthmaDialog({
  row,
  onClose,
}: {
  row: AsthmaRow;
  onClose: () => void;
}) {
  const a = useChronicAction(chronicApi.update);
  const [inhalers, setInhalers] = useState(row.inhalers ?? '');
  const [plan, setPlan] = useState(
    row.actionPlanAt ? clinicDay(row.actionPlanAt) : '',
  );
  return (
    <FormDialog
      title={`Inhalers and action plan · ${row.patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={!!plan && plan > today()}
      onSubmit={() =>
        a.mutateAsync({
          id: row.enrolmentId,
          inhalers: inhalers.trim(),
          actionPlanAt: plan ? `${plan}T00:00:00.000Z` : undefined,
        })
      }
    >
      <Field label="Inhalers">
        <Input
          value={inhalers}
          maxLength={200}
          onChange={(e) => setInhalers(e.target.value)}
        />
      </Field>
      <Field label="Action plan given on">
        <Input
          type="date"
          max={today()}
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}

function EndDialog({
  row,
  onClose,
}: {
  row: RegisterRow;
  onClose: () => void;
}) {
  const a = useChronicAction(chronicApi.end);
  const [reason, setReason] = useState('');
  return (
    <FormDialog
      title={`Remove from the ${CONDITION_LABEL[row.condition].toLowerCase()} register · ${row.patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={reason.trim().length < 5}
      submitLabel="Remove"
      onSubmit={() =>
        a.mutateAsync({ id: row.enrolmentId, reason: reason.trim() })
      }
    >
      <p className={styles.notice}>
        The enrolment and its checks stay on record; the patient can be enrolled
        again later.
      </p>
      <Field label="Reason (at least 5 characters)">
        <Input
          value={reason}
          maxLength={300}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}

function SyncDialog({
  patient,
  onClose,
}: {
  patient: PatientRef;
  onClose: () => void;
}) {
  const a = useChronicAction(chronicApi.sync);
  const [date, setDate] = useState('');
  return (
    <FormDialog
      title={`Line up refill dates · ${patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={!date}
      onSubmit={() =>
        a.mutateAsync({ patientId: patient.id, refillDate: date })
      }
    >
      <p className={styles.notice}>
        Records one refill day for all chronic medicines. The next prescription
        quantities are adjusted by the doctor.
      </p>
      <Field label="Common refill date">
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}

function ApproveDialog({ onClose }: { onClose: () => void }) {
  const a = useChronicAction(chronicApi.approve);
  const cfg = useChronicConfig();
  const [note, setNote] = useState('');
  const c = cfg.data;
  return (
    <FormDialog
      title="Record clinical sign-off"
      onClose={onClose}
      pending={a.isPending}
      disabled={note.trim().length < 5 || !c}
      submitLabel="Record sign-off"
      onSubmit={() => a.mutateAsync({ note: note.trim() })}
    >
      {c && (
        <dl className={styles.kv}>
          <dt>Diabetes</dt>
          <dd>
            HbA1c ≤ {c.diabetes.hba1cTargetPct} %, BP ≤{' '}
            {c.diabetes.bpTarget.systolic}/{c.diabetes.bpTarget.diastolic};
            HbA1c every {c.diabetes.hba1cEveryMonths} months; eye/foot/kidney
            every {c.diabetes.eyeEveryMonths}/{c.diabetes.footEveryMonths}/
            {c.diabetes.uacrEveryMonths} months
          </dd>
          <dt>Hypertension</dt>
          <dd>
            Below {c.hypertension.bpTarget.systolic}/
            {c.hypertension.bpTarget.diastolic}; review every{' '}
            {c.hypertension.reviewEveryMonths} months
          </dd>
          <dt>Asthma</dt>
          <dd>
            ACT ≥ {c.asthma.actWellControlledMin}; review every{' '}
            {c.asthma.reviewEveryMonths} months
          </dd>
          <dt>Heart risk</dt>
          <dd>
            Moderate from {c.heartRisk.moderatePct} %, high from{' '}
            {c.heartRisk.highPct} %
          </dd>
        </dl>
      )}
      <Field label="Sign-off note (who approved, reference)">
        <Input
          value={note}
          maxLength={300}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}
